import { useId, useMemo, useState } from 'react';

import { cn } from '@/lib/cn';

/**
 * The chart set. Hand-drawn SVG, no library.
 *
 * ── WHY NOT RECHARTS ────────────────────────────────────────────────────────
 *
 * The obvious answer costs 100kb or more before a single pixel is drawn, on an
 * app built for a ₦15,000 Android over a network that charges by the megabyte.
 * These four components are a few kilobytes, they take the brand colour from
 * CSS rather than baking it in, and none of them re-layouts on resize because
 * every one of them is a viewBox that simply scales.
 *
 * ── WHAT MAKES THEM READ AS EXPENSIVE ───────────────────────────────────────
 *
 * Not decoration — restraint, and then three details done properly:
 *
 *   a gradient that fades to nothing, so the area reads as depth rather than
 *   as a block of colour;
 *
 *   a curve through the points instead of straight segments, because real
 *   trade does not turn corners;
 *
 *   and a crosshair that snaps to the nearest day, so the number under your
 *   finger is a fact and not an estimate.
 *
 * No gridlines, no axis furniture, no legend. Everything removed from a chart
 * is something the eye no longer has to skip past to reach the line.
 *
 * ── AND WHY THE NUMBERS ARRIVE PRE-FORMATTED ────────────────────────────────
 *
 * Money crosses the wire as integer kobo and as a display string (§9.10). These
 * draw the integer and print the string: nothing here divides by 100, because
 * a chart is not a licence to invent a float.
 */

export interface Point {
  date: string;
  label: string;
  value: number;
}

/** A smooth path through the points, clamped so it never overshoots. */
function curve(points: Array<{ x: number; y: number }>): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0]!.x} ${points[0]!.y}`;

  let d = `M ${points[0]!.x} ${points[0]!.y}`;

  for (let i = 0; i < points.length - 1; i += 1) {
    const a = points[i]!;
    const b = points[i + 1]!;
    // A monotone-ish cubic: control points sit on the midpoint horizontally
    // and on their own end vertically, which curves the line without letting
    // it bulge past a value that never happened.
    const mx = (a.x + b.x) / 2;

    d += ` C ${mx} ${a.y}, ${mx} ${b.y}, ${b.x} ${b.y}`;
  }

  return d;
}

/**
 * The headline chart: a filled area under a curve, with a snapping crosshair.
 */
export function AreaChart({
  points,
  height = 132,
  format,
  className,
  tone = 'blue',
}: {
  points: Point[];
  height?: number;
  /** Turns a raw value into the string shown in the readout. */
  format?: (value: number) => string;
  className?: string;
  tone?: 'blue' | 'verified' | 'caution';
}) {
  const id = useId();
  const [hover, setHover] = useState<number | null>(null);

  const W = 600;
  const H = height;
  const PAD = 6;

  const { path, area, coords, max } = useMemo(() => {
    const values = points.map((p) => p.value);
    // A flat series still needs a baseline to sit on, or every point lands on
    // the same pixel and the chart looks broken rather than quiet.
    const top = Math.max(1, ...values);
    const step = points.length > 1 ? (W - PAD * 2) / (points.length - 1) : 0;

    const coords = points.map((p, i) => ({
      x: PAD + i * step,
      y: PAD + (1 - p.value / top) * (H - PAD * 2),
    }));

    const path = curve(coords);
    const last = coords[coords.length - 1];
    const first = coords[0];

    const area =
      first && last ? `${path} L ${last.x} ${H} L ${first.x} ${H} Z` : '';

    return { path, area, coords, max: top };
  }, [points, H]);

  const active = hover !== null ? points[hover] : null;
  const activeAt = hover !== null ? coords[hover] : null;

  if (points.length === 0) return null;

  return (
    <div className={cn('relative', className)}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="block w-full"
        style={{ height }}
        role="img"
        aria-label={`Trend over ${points.length} days, highest ${format ? format(max) : max}`}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          const ratio = (event.clientX - box.left) / box.width;
          // Snap to the nearest day. A readout that interpolates between two
          // days is a number that never happened.
          setHover(
            Math.max(0, Math.min(points.length - 1, Math.round(ratio * (points.length - 1)))),
          );
        }}
      >
        <defs>
          <linearGradient id={`fill-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={`var(--color-${tone})`} stopOpacity="0.26" />
            <stop offset="70%" stopColor={`var(--color-${tone})`} stopOpacity="0.04" />
            <stop offset="100%" stopColor={`var(--color-${tone})`} stopOpacity="0" />
          </linearGradient>
        </defs>

        <path d={area} fill={`url(#fill-${id})`} />

        <path
          d={path}
          fill="none"
          stroke={`var(--color-${tone})`}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          // The line is drawn in a viewBox that stretches horizontally, so
          // without this it would be thicker at the top than at the sides.
          vectorEffect="non-scaling-stroke"
        />

        {activeAt ? (
          <>
            <line
              x1={activeAt.x}
              y1={0}
              x2={activeAt.x}
              y2={H}
              stroke="var(--color-line)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
            <circle
              cx={activeAt.x}
              cy={activeAt.y}
              r="4"
              fill="var(--color-paper)"
              stroke={`var(--color-${tone})`}
              strokeWidth="2.5"
              vectorEffect="non-scaling-stroke"
            />
          </>
        ) : null}
      </svg>

      {/* The readout. Absolutely placed so it never changes the chart's
          height — a tooltip that reflows the page under the cursor is a
          tooltip people learn to avoid. */}
      <div className="pointer-events-none mt-1.5 flex h-[15px] items-center justify-between text-[11px] text-faint">
        {active ? (
          <span className="font-medium text-ink">
            {active.label} · {format ? format(active.value) : active.value}
          </span>
        ) : (
          <>
            <span>{points[0]?.label}</span>
            <span>{points[points.length - 1]?.label}</span>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * A tiny inline trend, for a stat card.
 *
 * No axis, no interaction — it exists to say "and it has been going like this",
 * which is a shape rather than a number.
 */
export function Sparkline({
  points,
  className,
  tone = 'blue',
}: {
  points: Point[];
  className?: string;
  tone?: 'blue' | 'verified' | 'caution' | 'danger';
}) {
  const W = 120;
  const H = 28;

  const path = useMemo(() => {
    if (points.length === 0) return '';

    const top = Math.max(1, ...points.map((p) => p.value));
    const step = points.length > 1 ? W / (points.length - 1) : 0;

    return curve(
      points.map((p, i) => ({ x: i * step, y: 2 + (1 - p.value / top) * (H - 4) })),
    );
  }, [points]);

  if (points.length === 0) return null;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-hidden
      className={cn('block', className)}
    >
      <path
        d={path}
        fill="none"
        stroke={`var(--color-${tone})`}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/**
 * Bars, for comparing a handful of named things.
 *
 * Horizontal rather than vertical on purpose: the labels are product names and
 * pharmacy names, and rotating those to fit under a vertical bar is how a chart
 * becomes unreadable on a phone.
 */
export function BarList({
  rows,
  className,
}: {
  rows: Array<{ label: string; value: number; display: string; hint?: string }>;
  className?: string;
}) {
  const top = Math.max(1, ...rows.map((r) => r.value));

  return (
    <div className={cn('space-y-2.5', className)}>
      {rows.map((row) => (
        <div key={row.label}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-[13px] font-medium text-ink">{row.label}</span>
            <span className="shrink-0 font-mono text-[12.5px] font-bold tabular-nums text-ink">
              {row.display}
            </span>
          </div>

          <div className="mt-1 flex items-center gap-2">
            <span className="h-[7px] min-w-0 flex-1 overflow-hidden rounded-pill bg-paper">
              <span
                className="block h-full rounded-pill bg-blue transition-[width] duration-500 ease-out"
                style={{ width: `${Math.max(2, (row.value / top) * 100)}%` }}
              />
            </span>
            {row.hint ? (
              <span className="shrink-0 text-[11px] text-faint">{row.hint}</span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * A single proportion, drawn as an arc.
 *
 * For the rates — acceptance, replies, answers — where the interesting thing is
 * how close to whole it is, and a bar of 94% next to a bar of 97% is two bars
 * nobody can tell apart.
 */
export function Gauge({
  bps,
  label,
  size = 96,
  tone,
}: {
  /** Basis points, as every rate crosses the wire. Null when there is no data. */
  bps: number | null;
  label: string;
  size?: number;
  tone?: 'blue' | 'verified' | 'caution' | 'danger';
}) {
  const pct = bps === null ? 0 : Math.max(0, Math.min(100, bps / 100));
  const R = 42;
  const C = 2 * Math.PI * R;

  // Chosen from the number rather than passed in, so the same rate is never
  // green on one screen and amber on another.
  const resolved = tone ?? (pct >= 90 ? 'verified' : pct >= 70 ? 'caution' : 'danger');

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative" style={{ width: size, height: size }}>
        <svg viewBox="0 0 100 100" className="block h-full w-full -rotate-90">
          <circle cx="50" cy="50" r={R} fill="none" stroke="var(--color-paper)" strokeWidth="9" />
          {bps === null ? null : (
            <circle
              cx="50"
              cy="50"
              r={R}
              fill="none"
              stroke={`var(--color-${resolved})`}
              strokeWidth="9"
              strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={C - (C * pct) / 100}
              className="transition-[stroke-dashoffset] duration-700 ease-out"
            />
          )}
        </svg>

        <span className="absolute inset-0 grid place-items-center">
          <span className="font-display text-[19px] font-bold tracking-[-0.02em] text-ink">
            {bps === null ? '—' : `${Math.round(pct)}%`}
          </span>
        </span>
      </div>

      <span className="text-center text-[11.5px] leading-snug text-muted">{label}</span>
    </div>
  );
}
